import type { DashboardStats } from '../../../hooks/useDashboardData';
import { useNavigate } from 'react-router-dom';

interface Props {
  stats: DashboardStats;
  loading: boolean;
}

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl p-5 border border-gray-100 animate-pulse">
    <div className="flex items-start justify-between mb-4">
      <div className="w-11 h-11 bg-gray-100 rounded-xl" />
    </div>
    <div className="h-3 bg-gray-100 rounded w-24 mb-2" />
    <div className="h-6 bg-gray-100 rounded w-32 mb-1" />
    <div className="h-3 bg-gray-100 rounded w-20" />
  </div>
);

export const StatsCards = ({ stats, loading }: Props) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  const hasPendingExpenses = stats.pendingMonthlyExpenses > 0;
  const hasPendingIncome   = stats.pendingMonthlyIncome > 0;

  const cards = [
    {
      label: 'Saldo Realizado',
      value: fmt(stats.totalBalance),
      icon: 'ri-wallet-3-line',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      valueColor: stats.totalBalance >= 0 ? 'text-gray-900' : 'text-red-600',
      sub: 'apenas lançamentos pagos',
      badge: null,
      onClick: () => navigate('/contas'),
    },
    {
      label: 'Receitas do Mês',
      value: fmt(stats.monthlyIncome),
      icon: 'ri-arrow-up-circle-line',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      valueColor: 'text-gray-900',
      sub: stats.monthlyIncome === 0 ? 'Nenhuma receita recebida' : 'recebidas',
      badge: hasPendingIncome
        ? { label: `+ ${fmt(stats.pendingMonthlyIncome)} a receber`, color: 'text-amber-600 bg-amber-50' }
        : null,
      onClick: () => navigate('/lancamentos'),
    },
    {
      label: 'Despesas do Mês',
      value: fmt(stats.monthlyExpenses),
      icon: 'ri-arrow-down-circle-line',
      iconBg: 'bg-red-100',
      iconColor: 'text-red-500',
      valueColor: 'text-gray-900',
      sub: stats.monthlyExpenses === 0 ? 'Nenhuma despesa paga' : 'pagas',
      badge: hasPendingExpenses
        ? { label: `+ ${fmt(stats.pendingMonthlyExpenses)} a pagar`, color: 'text-amber-600 bg-amber-50' }
        : null,
      onClick: () => navigate('/lancamentos'),
    },
    {
      label: 'Contas Pendentes',
      value: fmt(stats.pendingBillsTotal),
      icon: 'ri-bill-line',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      valueColor: stats.pendingBillsTotal > 0 ? 'text-amber-700' : 'text-gray-900',
      sub:
        stats.pendingBillsCount === 0
          ? 'Nenhuma conta pendente'
          : `${stats.pendingBillsCount} conta${stats.pendingBillsCount > 1 ? 's' : ''} nos próximos 30 dias`,
      badge: null,
      onClick: () => navigate('/lancamentos'),
    },
    {
      label: 'Metas Ativas',
      value: `${stats.activeGoalsCount}`,
      icon: 'ri-focus-3-line',
      iconBg: 'bg-violet-100',
      iconColor: 'text-violet-600',
      valueColor: 'text-gray-900',
      sub: stats.activeGoalsCount === 0 ? 'Crie sua primeira meta' : 'em andamento',
      badge: null,
      onClick: () => navigate('/metas'),
    },
  ];

  return (
    <div className="grid grid-cols-2 xl:grid-cols-5 gap-4">
      {cards.map((card, i) => (
        <div
          key={i}
          onClick={card.onClick}
          className="bg-white rounded-2xl p-5 border border-gray-100 cursor-pointer hover:border-emerald-200 transition-colors group flex flex-col"
        >
          {/* Icon row */}
          <div className="flex items-start justify-between mb-4">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.iconBg} shrink-0`}>
              <i className={`${card.icon} text-xl ${card.iconColor}`} />
            </div>
          </div>

          {/* Label */}
          <p className="text-xs font-500 text-gray-500 mb-1">{card.label}</p>

          {/* Main value */}
          <p className={`text-xl font-700 leading-tight ${card.valueColor}`}>{card.value}</p>

          {/* Sub text */}
          <p className="text-xs text-gray-400 mt-1">{card.sub}</p>

          {/* Pending badge */}
          {card.badge && (
            <div className={`mt-2.5 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-600 w-fit ${card.badge.color}`}>
              <i className="ri-time-line text-[11px]" />
              {card.badge.label}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
